export interface SupportMessageType {
  _id: string;
  body: string;
  senderType?: 'user' | 'admin' | 'support' | 'agent' | string;
  sender?: string;
  role?: string;
  isAdmin?: boolean;
  isSupport?: boolean;
  readAt?: string | null;
  isRead?: boolean;
  createdAt: string;
  updatedAt?: string;
  threadId?: string;
}

export interface SupportThreadType {
  _id: string;
  subject?: string;
  title?: string;
  lastMessage?: string | SupportMessageType;
  unreadCount?: number;
  status?: string;
  createdAt: string;
  updatedAt?: string;
}

export function isSupportMessageFromUser(message: SupportMessageType): boolean {
  const senderType = (message.senderType || message.sender || message.role || '')
    .toString()
    .toLowerCase();

  if (message.isAdmin || message.isSupport) {
    return false;
  }

  if (
    senderType.includes('admin') ||
    senderType.includes('support') ||
    senderType.includes('agent') ||
    senderType.includes('staff')
  ) {
    return false;
  }

  return true;
}

export function getLastMessagePreview(thread?: SupportThreadType | null): string {
  if (!thread) return 'Chat with our support team';
  if (typeof thread.lastMessage === 'string' && thread.lastMessage.trim()) {
    return thread.lastMessage;
  }
  if (
    thread.lastMessage &&
    typeof thread.lastMessage === 'object' &&
    thread.lastMessage.body
  ) {
    return thread.lastMessage.body;
  }
  if (thread.subject || thread.title) {
    return thread.subject || thread.title || 'Open your support chat';
  }
  return 'Continue your conversation with support';
}

/** Maps a `support:message` socket payload onto the REST message shape. */
export function mapSocketSupportMessage(payload: unknown): SupportMessageType | null {
  const raw = Array.isArray(payload) ? payload[0] : payload;
  if (!raw || typeof raw !== 'object') return null;

  const data = (raw as { data?: unknown }).data ?? raw;
  if (!data || typeof data !== 'object') return null;

  const record = data as Record<string, unknown>;
  const id = String(record.messageId || record._id || '');
  const body = typeof record.body === 'string' ? record.body : '';
  if (!id && !body) return null;

  return {
    _id: id || `socket-${Date.now()}`,
    body,
    senderType: typeof record.senderType === 'string' ? record.senderType : undefined,
    sender: typeof record.senderId === 'string' ? record.senderId : undefined,
    createdAt:
      typeof record.createdAt === 'string'
        ? record.createdAt
        : new Date().toISOString(),
    threadId: typeof record.threadId === 'string' ? record.threadId : undefined,
    isRead: Boolean(record.isRead),
    readAt: typeof record.readAt === 'string' ? record.readAt : null,
  };
}

export function sortSupportMessages(items: SupportMessageType[]) {
  return [...items].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

export function mergeSupportMessages(
  current: SupportMessageType[],
  incoming: SupportMessageType[]
) {
  const unique = new Map<string, SupportMessageType>();
  [...current, ...incoming].forEach((msg) => {
    if (msg?._id) unique.set(msg._id, msg);
  });
  return sortSupportMessages(Array.from(unique.values()));
}
